import { useMemo, useState } from "react";
import { GraduationCap } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { GrowthArea } from "@/components/tools/charts";
import { educationPlanner } from "@/lib/calculators";

export default function EducationPlanner() {
  const [currentCost, setCost] = useState(2000000);
  const [years, setYears] = useState(12);
  const [inflation, setInflation] = useState(10);
  const [rate, setRate] = useState(12);
  const [savings, setSavings] = useState(300000);

  const r = useMemo(
    () => educationPlanner({ currentCost, years, inflation, rate, currentSavings: savings }),
    [currentCost, years, inflation, rate, savings]
  );

  return (
    <ToolLayout title="Education Planner" subtitle="Beat education inflation and know the SIP to start today" icon={GraduationCap} accent="#D96B43">
      <TwoCol
        left={
          <SectionCard title="The goal">
            <div className="space-y-5">
              <Field label="Course cost today"><MoneyInput testid="edu-cost" value={currentCost} onChange={setCost} helper={compactINR(currentCost)} /></Field>
              <Field label="Years until admission" hint={years + " yrs"}><SliderRow testid="edu-years" value={years} min={1} max={25} suffix=" yr" onChange={setYears} /></Field>
              <Field label="Education inflation (p.a.)" hint={inflation + "%"}><SliderRow testid="edu-inflation" value={inflation} min={4} max={15} step={0.5} suffix="%" onChange={setInflation} /></Field>
              <Field label="Expected return on investment (p.a.)" hint={rate + "%"}><SliderRow testid="edu-rate" value={rate} min={4} max={18} step={0.5} suffix="%" onChange={setRate} /></Field>
              <Field label="Already saved for this"><MoneyInput testid="edu-savings" value={savings} onChange={setSavings} helper={compactINR(savings)} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard testid="edu-sip" label="Start this monthly SIP" value={compactINR(r.requiredSip)} accent="#D96B43" big />
              <StatCard testid="edu-lumpsum" label="Or invest lumpsum today" value={compactINR(r.requiredLumpsum)} accent="#2D6A4F" />
            </div>
            <SectionCard title="How fees balloon with inflation">
              <GrowthArea data={r.points} keys={[{ key: "value", name: "Projected fee", color: "#D96B43" }]} />
            </SectionCard>
            <SectionCard title="The maths">
              <InfoRow label="Cost today" value={compactINR(currentCost)} />
              <InfoRow label={`Cost in ${years} yrs (@ ${inflation}%)`} value={compactINR(r.futureCost)} strong />
              <InfoRow label="Your savings will grow to" value={compactINR(r.fvSavings)} />
              <InfoRow label="Gap to fund" value={compactINR(r.need)} strong />
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
